import { useMemo, useState } from 'react';

import { ApiError, api } from '../../api/client';
import type { Diagnostic, Lock, RunDetail } from '../../api/types';
import { Banner, Card, Empty, PriorityBadge, formatDate, secondaryLabel } from '../../components/ui';
import { useAsync } from '../../hooks/useAsync';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function inThreeMonths(): string {
  const date = new Date();
  date.setMonth(date.getMonth() + 3);
  return date.toISOString().slice(0, 10);
}

/**
 * Écran de génération (brief §13, §14, §15).
 *
 * Le principe du §23 gouverne l'ergonomie : l'ordinateur fait le travail
 * combinatoire, le responsable vérifie, fige ce qui lui convient d'un clic,
 * régénère le reste, puis publie.
 */
export default function PlanningScreen() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(inThreeMonths());
  const [run, setRun] = useState<RunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const roles = useAsync(() => api.roles(), []);
  const events = useAsync(() => api.events(), []);
  const locks = useAsync<Lock[]>(() => api.locks(), []);

  const roleName = useMemo(() => {
    const index = new Map((roles.data ?? []).map((role) => [role.id, role.name]));
    return (roleId: number) => index.get(roleId) ?? `rôle ${roleId}`;
  }, [roles.data]);

  // Les rôles doivent apparaître dans le **même ordre** à chaque date, sinon
  // la grille est illisible : on suit l'ordre d'affichage du référentiel.
  const roleRank = useMemo(() => {
    const index = new Map(
      (roles.data ?? []).map((role, position) => [role.id, role.display_order * 1000 + position]),
    );
    return (roleId: number) => index.get(roleId) ?? Number.MAX_SAFE_INTEGER;
  }, [roles.data]);

  const personName = useMemo(() => {
    const index = new Map(
      (run?.per_person ?? []).map((person) => [person.person_id, person.name]),
    );
    return (personId: number) => index.get(personId) ?? `#${personId}`;
  }, [run]);

  const eventLabel = useMemo(() => {
    const index = new Map((events.data ?? []).map((event) => [event.id, event]));
    return (eventId: number) => index.get(eventId) ?? null;
  }, [events.data]);

  const guard = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.detail : 'Action impossible.');
    } finally {
      setBusy(false);
    }
  };

  const generate = () =>
    guard(async () => {
      setRun(await api.createRun({ horizon_start: from, horizon_end: to }));
      locks.reload();
    });

  const regenerate = () =>
    guard(async () => {
      if (run === null) return;
      setRun(await api.regenerate(run.id));
      setNotice('Planning régénéré : les affectations figées ont été conservées.');
    });

  const publish = () =>
    guard(async () => {
      if (run === null) return;
      await api.publish(run.id);
      setRun({ ...run, plan_status: 'PUBLISHED' });
      setNotice('Planning publié : les membres le voient désormais.');
    });

  /** Figer ou libérer une affectation, puis laisser le responsable régénérer. */
  const toggleLock = (eventId: number, personId: number, roleId: number) =>
    guard(async () => {
      const existing = (locks.data ?? []).find(
        (lock) =>
          lock.event_id === eventId &&
          lock.person_id === personId &&
          lock.role_id === roleId,
      );
      if (existing) await api.deleteLock(existing.id);
      else await api.createLock({ event_id: eventId, person_id: personId, role_id: roleId });
      locks.reload();
      setNotice(
        existing
          ? 'Affectation libérée. Régénérez pour laisser le moteur la recalculer.'
          : 'Affectation figée. Régénérez pour recalculer le reste autour.',
      );
    });

  const isLocked = (eventId: number, personId: number, roleId: number) =>
    (locks.data ?? []).some(
      (lock) =>
        lock.event_id === eventId && lock.person_id === personId && lock.role_id === roleId,
    );

  const grid = useMemo(() => {
    if (run === null) return [];
    const byEvent = new Map<number, Map<number, number[]>>();
    for (const assignment of run.assignments ?? []) {
      const roleMap = byEvent.get(assignment.event_id) ?? new Map<number, number[]>();
      roleMap.set(assignment.role_id, [
        ...(roleMap.get(assignment.role_id) ?? []),
        assignment.person_id,
      ]);
      byEvent.set(assignment.event_id, roleMap);
    }
    return [...byEvent.entries()]
      .map(
        ([eventId, roleMap]) =>
          [
            eventId,
            [...roleMap.entries()].sort(
              (left, right) => roleRank(left[0]) - roleRank(right[0]),
            ),
          ] as const,
      )
      .sort((left, right) => {
        const a = eventLabel(left[0])?.starts_at ?? '';
        const b = eventLabel(right[0])?.starts_at ?? '';
        return a.localeCompare(b);
      });
  }, [run, eventLabel, roleRank]);

  return (
    <>
      <h1>Générer le planning</h1>
      <p className="muted">
        L'ordinateur propose, vous décidez : figez ce qui vous convient, régénérez le reste,
        puis publiez.
      </p>

      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="success">{notice}</Banner>}

      <Card>
        <div className="row">
          <div>
            <label htmlFor="from">Du</label>
            <input id="from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </div>
          <div>
            <label htmlFor="to">Au</label>
            <input id="to" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </div>
          <button className="primary" onClick={generate} disabled={busy}>
            {busy ? 'Calcul en cours…' : 'Générer le planning'}
          </button>
          {run !== null && run.status !== 'INFEASIBLE' && (
            <>
              <button onClick={regenerate} disabled={busy}>
                Régénérer
              </button>
              <button
                onClick={publish}
                disabled={busy || run.plan_status !== 'DRAFT'}
                title={run.plan_status !== 'DRAFT' ? 'Ce planning est déjà publié.' : undefined}
              >
                Publier
              </button>
            </>
          )}
        </div>
      </Card>

      {run === null ? (
        <Card>
          <Empty>Choisissez une période, puis lancez la génération.</Empty>
        </Card>
      ) : run.diagnostic ? (
        <DiagnosticPanel diagnostic={run.diagnostic} />
      ) : (
        <>
          <Card
            title="Planning proposé"
            actions={
              <span className={`badge ${run.plan_status === 'PUBLISHED' ? 'ok' : 'optional'}`}>
                {run.plan_status === 'PUBLISHED' ? 'publié' : 'brouillon'}
              </span>
            }
          >
            <p className="muted">
              Cliquez sur un nom pour le figer : la régénération le conservera et recalculera
              le reste autour.
            </p>
            {grid.length === 0 ? (
              <Empty>Aucune affectation sur cette période.</Empty>
            ) : (
              <div className="stack">
                {grid.map(([eventId, roleRows]) => {
                  const event = eventLabel(eventId);
                  const extra = event ? secondaryLabel(event.title, event.starts_at) : null;
                  return (
                    <div key={eventId}>
                      <h3>
                        {event ? formatDate(event.starts_at) : `#${eventId}`}
                        {extra && <span className="muted"> — {extra}</span>}
                      </h3>
                      <div className="stack">
                        {roleRows.map(([roleId, people]) => (
                          <div key={roleId} className="spread">
                            <span className="muted" style={{ minWidth: 140 }}>
                              {roleName(roleId)}
                            </span>
                            <span className="slot">
                              {people.map((personId) => {
                                const locked = isLocked(eventId, personId, roleId);
                                return (
                                  <button
                                    key={personId}
                                    className={`chip${locked ? ' locked' : ''}`}
                                    disabled={busy}
                                    aria-pressed={locked}
                                    onClick={() => toggleLock(eventId, personId, roleId)}
                                    title={locked ? 'Libérer cette affectation' : 'Figer cette affectation'}
                                  >
                                    {personName(personId)}
                                    {locked && <span aria-hidden="true">🔒</span>}
                                  </button>
                                );
                              })}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <QualityPanel run={run} />
        </>
      )}
    </>
  );
}

/**
 * Qualité du planning, en mots.
 *
 * Les valeurs sont celles du planning **livré**, et les libellés viennent du
 * serveur : le §17 interdit d'exposer le jargon d'optimisation, et un libellé
 * produit au même endroit que la valeur ne peut pas être faux.
 */
function QualityPanel({ run }: { run: RunDetail }) {
  const uncovered = run.uncovered ?? [];
  const tiers = run.tiers ?? [];

  return (
    <Card title="Qualité du planning">
      <div className="row" style={{ marginBottom: 12 }}>
        <span className={`badge ${run.status === 'OPTIMAL' ? 'ok' : 'preferred'}`}>
          {run.status === 'OPTIMAL' ? 'meilleure solution trouvée' : 'solution acceptable'}
        </span>
        {run.solver_wall_time_ms !== null && (
          <span className="muted">calculé en {(run.solver_wall_time_ms / 1000).toFixed(1)} s</span>
        )}
        {run.unconfirmed_assignments ? (
          <span className="badge preferred">
            {run.unconfirmed_assignments} affectation(s) sans confirmation
          </span>
        ) : null}
      </div>

      <table>
        <tbody>
          {tiers
            .filter((tier) => tier.name !== 'tie_break')
            .map((tier) => (
              <tr key={tier.name}>
                <td>{tier.label}</td>
                <td style={{ width: 60, textAlign: 'right' }}>
                  <span className={`badge ${tier.value === 0 ? 'ok' : 'preferred'}`}>
                    {tier.value}
                  </span>
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {uncovered.length > 0 && (
        <>
          <h3 style={{ marginTop: 16 }}>Postes non pourvus</h3>
          <div className="table-scroll">
            <table>
              <tbody>
                {uncovered.map((slot) => (
                  <tr key={`${slot.event_id}-${slot.role_id}`}>
                    <td>{slot.event_label}</td>
                    <td>{slot.role_name}</td>
                    <td>
                      <PriorityBadge priority={slot.priority} />
                    </td>
                    <td style={{ textAlign: 'right' }}>−{slot.missing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3 style={{ marginTop: 16 }}>Charge par personne</h3>
      <div className="table-scroll">
        <table>
          <tbody>
            {(run.per_person ?? [])
              .filter((person) => person.capacity > 0)
              .sort((left, right) => right.services - left.services)
              .map((person) => (
                <tr key={person.person_id}>
                  <td>{person.name}</td>
                  <td style={{ width: 90 }} className="muted">
                    {person.services} / {person.capacity}
                  </td>
                  <td>
                    <div className="meter" aria-hidden="true">
                      <span
                        style={{
                          width: `${Math.round(
                            (person.services / Math.max(1, person.capacity)) * 100,
                          )}%`,
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <p className="muted" style={{ marginTop: 8 }}>
        « 3 / 8 » se lit : trois services pour huit dates où la personne était disponible.
      </p>
    </Card>
  );
}

/**
 * Explication d'un planning impossible (brief §15).
 *
 * Le poste, la personne et le motif, puis des assouplissements **vérifiés**
 * comme suffisants — jamais un « INFEASIBLE » nu.
 */
function DiagnosticPanel({ diagnostic }: { diagnostic: Diagnostic }) {
  return (
    <>
      <Banner kind="error">{diagnostic.message}</Banner>

      {(diagnostic.blocking_requirements ?? []).map((blocking) => (
        <Card
          key={`${blocking.event_id}-${blocking.role_id}`}
          title={`${blocking.event_label} — ${blocking.role_name}`}
        >
          <p className="muted">
            Il faut {blocking.minimum} personne(s) ; {blocking.eligible_count} peuvent y être
            affectées.
          </p>
          <table>
            <tbody>
              {(blocking.excluded ?? []).map((person) => (
                <tr key={person.person_id}>
                  <td style={{ width: '35%' }}>{person.name}</td>
                  <td className="muted">{person.detail || person.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}

      {(diagnostic.involved_constraints ?? []).length > 0 && (
        <Card title="Règles impliquées">
          <ul className="muted" style={{ margin: 0, paddingLeft: 18 }}>
            {(diagnostic.involved_constraints ?? []).map((constraint) => (
              <li key={constraint}>{constraint}</li>
            ))}
          </ul>
        </Card>
      )}

      {(diagnostic.suggested_relaxations ?? []).length > 0 && (
        <Card title="Ce qui débloquerait la situation">
          <ul className="stack" style={{ listStyle: 'none', padding: 0 }}>
            {(diagnostic.suggested_relaxations ?? []).map((relaxation, index) => (
              <li key={index}>{relaxation.message}</li>
            ))}
          </ul>
          <p className="muted" style={{ marginTop: 8 }}>
            Chaque piste a été vérifiée : appliquée seule, elle rend un planning possible.
          </p>
        </Card>
      )}
    </>
  );
}
